import { createContext, useContext, onMount } from "solid-js";
import { createStore } from "solid-js/store";
import socket from "./socket";

const QuizContext = createContext();

export function QuizContextProvider(props: any) {
  const [quizStore, setQuizStore] = createStore({
    quizzes: [] as any[],
    loading: false
  });

  onMount(() => {
    console.log('Mounted QuizContext');
    
    socket.on("quiz-list", (quizzes: any) => {
      setQuizStore({ quizzes, loading: false });
    });
  });

  const runOperation = (
    event: 'create-quiz' | 'update-quiz' | 'delete-quiz' | 'archive-quiz' | 'unarchive-quiz' | 'mark-quiz-deleted',
    data: unknown
  ) => new Promise<{ quizId?: number }>((resolve, reject) => {
    const send = (attempt: number) => {
      socket.timeout(8_000).emit(event, data, (timeoutError: Error | null, result?: { ok: boolean; error?: string; quizId?: number }) => {
        if (timeoutError && attempt === 0 && event === 'create-quiz') {
          send(1); // The same request ID makes a create retry safe.
          return;
        }
        if (timeoutError) {
          setQuizStore({ loading: false });
          reject(new Error('The server did not acknowledge the operation'));
          return;
        }
        if (!result?.ok) {
          setQuizStore({ loading: false });
          reject(new Error(result?.error || 'Operation failed'));
          return;
        }
        resolve({ quizId: result.quizId });
      });
    };
    send(0);
  });

  const actions = {
    createQuiz: (fields: { title: string; markdown: string; state?: string; settings?: string; hideQuestionsFromStudents?: boolean }) => {
      setQuizStore({ loading: true });
      return runOperation('create-quiz', { ...fields, requestId: crypto.randomUUID().replaceAll('-', '') });
    },
    updateQuiz: (id: number, fields: Partial<{ title: string; markdown: string; state: string; settings: string; quizId: string; hideQuestionsFromStudents: boolean }>) => {
      setQuizStore({ loading: true });
      return runOperation('update-quiz', { id, fields });
    },
    deleteQuiz: (id: number) => {
      if (confirm('Are you sure you want to permanently delete this quiz? This cannot be undone.')) {
        setQuizStore({ loading: true });
        return runOperation('delete-quiz', id);
      }
      return Promise.resolve();
    },
    archiveQuiz: (id: number) => {
      setQuizStore({ loading: true });
      return runOperation('archive-quiz', id);
    },
    unarchiveQuiz: (id: number) => {
      setQuizStore({ loading: true });
      return runOperation('unarchive-quiz', id);
    },
    markQuizDeleted: (id: number) => {
      if (confirm('Are you sure you want to delete this quiz? You can recover it later.')) {
        setQuizStore({ loading: true });
        return runOperation('mark-quiz-deleted', id);
      }
      return Promise.resolve();
    },
    downloadCSV: (quizId: number, quizTitle: string) => {
      return new Promise<void>((resolve, reject) => {
        const handleCSV = (data: { quizId: number; quizTitle: string; csv: string }) => {
          // Create a blob and download it
          const blob = new Blob([data.csv], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          const fileName = (data.quizTitle || quizTitle).replace(/[^a-z0-9]/gi, '_');
          link.setAttribute('href', url);
          link.setAttribute('download', `${fileName}_results.csv`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          socket.off('quiz-results-csv', handleCSV);
          socket.off('quiz-results-csv-error', handleError);
          resolve();
        };

        const handleError = (data: { error: string }) => {
          socket.off('quiz-results-csv', handleCSV);
          socket.off('quiz-results-csv-error', handleError);
          reject(new Error(data.error));
        };

        socket.on('quiz-results-csv', handleCSV);
        socket.on('quiz-results-csv-error', handleError);
        socket.emit('get-quiz-results-csv', { quizId });
      });
    }
  };

  return (
    <QuizContext.Provider value={[quizStore, setQuizStore, actions]}>
      {props.children}
    </QuizContext.Provider>
  );
}

export function useQuizContext() { 
  return useContext(QuizContext); 
}
